import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import './ErrorBoundary.scss';
import { useLocationsStore } from '../../hooks/useLocationsStore';

interface ErrorFallbackProps {
  error: Error;
  componentStack: string | null;
  resetError: () => void;
  isGlobal?: boolean;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, componentStack, resetError, isGlobal = false }) => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  const navigate = useNavigate();
  const currentLocationId = useLocationsStore(state => state.currentLocationId);

  const handleNavigateHome = () => {
    resetError();
    navigate('/');
  };

  const handleNavigateProfile = () => {
    resetError();
    navigate(`/app/location/${currentLocationId}/profile`);
  };

  return (
    <div className="error-boundary">
      <div className="error-boundary__card">
        {/* Alert Icon */}
        <svg className="error-boundary__icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
          <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <circle cx="12" cy="16" r="1" fill="currentColor" />
        </svg>

        <h1 className="error-boundary__title">Something went wrong</h1>
        <p className="error-boundary__message">
          We've been notified and are working on a fix. Try refreshing the page or use the navigation menu to continue.
        </p>

        <div className="error-boundary__actions">
          <>
            <button className="error-boundary__button error-boundary__button--primary" onClick={handleNavigateHome}>
              Navigate to Home
            </button>
            <button
              className="error-boundary__button error-boundary__button--secondary"
              onClick={handleNavigateProfile}
            >
              Navigate to Profile
            </button>
          </>
        </div>

        {isDevelopment && (
          <details className="error-boundary__details">
            <summary className="error-boundary__summary">Developer Details</summary>
            <div className="error-boundary__error">
              <strong>Error:</strong>
              <pre>{error.toString()}</pre>
            </div>
            {error.stack && (
              <div className="error-boundary__error">
                <strong>Stack:</strong>
                <pre>{error.stack}</pre>
              </div>
            )}
            {componentStack && (
              <div className="error-boundary__error">
                <strong>Component Stack:</strong>
                <pre>{componentStack}</pre>
              </div>
            )}
          </details>
        )}
      </div>
    </div>
  );
};

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  beforeCapture?: (scope: Sentry.Scope) => void;
  isGlobal?: boolean;
}

/**
 * Error Boundary Component with Sentry Integration
 *
 * This component catches JavaScript errors anywhere in the child component tree,
 * logs those errors to Sentry, and displays a fallback UI instead of the component
 * tree that crashed.
 *
 * @param isGlobal - If true, shows navigation options (Home, Profile). If false, shows recovery options (Try Again, Reload Page) (default: false)
 *
 * @example Global error boundary with navigation options
 * ```tsx
 * <ErrorBoundary isGlobal>
 *   <App />
 * </ErrorBoundary>
 * ```
 *
 * @example Local error boundary for critical components with recovery options
 * ```tsx
 * <ErrorBoundary>
 *   <CriticalComponent />
 * </ErrorBoundary>
 * ```
 *
 * @example With custom error handling
 * ```tsx
 * <ErrorBoundary
 *   isGlobal
 *   onError={(error, errorInfo) => {
 *     console.log('Custom error handler', error);
 *   }}
 *   beforeCapture={(scope) => {
 *     scope.setTag('location', 'custom');
 *   }}
 * >
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  children,
  fallback,
  onError,
  beforeCapture,
  isGlobal = false,
}) => {
  const location = useLocation();
  const errorBoundaryRef = useRef<Sentry.ErrorBoundary>(null);

  // Reset error boundary when location changes (for global error boundary on navigation)
  useEffect(() => {
    if (isGlobal && errorBoundaryRef.current) {
      // Use a key change to force remount of error boundary
      // This resets the error state when navigating
    }
  }, [location.pathname, isGlobal]);

  const CustomFallback = fallback || ((props: ErrorFallbackProps) => <ErrorFallback {...props} isGlobal={isGlobal} />);

  return (
    <Sentry.ErrorBoundary
      key={isGlobal ? location.pathname : undefined}
      ref={errorBoundaryRef}
      fallback={CustomFallback}
      onError={onError}
      beforeCapture={beforeCapture}
      showDialog={false}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default ErrorBoundary;
